import React, {useMemo} from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import {DeliveryGroup, Segment} from "../types";
import {EtaParams} from "../store";
import {DeliveryType} from "../../shared/types";

interface Props {
  open: boolean;
  onClose: () => void;
  etaParams: EtaParams;
  etaParamsLoaded: boolean;
  totalDistance: number;
  totalRoutingDuration: number;
  totalEtaDuration: number;
  finalPointName?: string;
  groupedDeliveries: DeliveryGroup[];
  addedSegments: Segment[];
  pointsDurationMapping: any[];
  auditEnabled: boolean;
  onCapture: () => void;
}

const formatDuration = (seconds: number) => {
  const roundedSeconds = Math.max(0, Math.floor(seconds || 0));
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const remainingSeconds = roundedSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const getClosestPointIndex = (record: DeliveryGroup, pointsDurationMapping: any[]) => {
  let bestIndex = 0;
  let score = Number.MAX_SAFE_INTEGER;
  const startIndex = record.id.toString().includes("endstore") ? 2 : 0;

  for (let index = startIndex; index < pointsDurationMapping.length; index += 1) {
    const point = pointsDurationMapping[index];
    const [longitude, latitude] = point.point.split(",").map(Number);
    const distance = Math.abs(longitude - record.longitude) + Math.abs(latitude - record.latitude);
    if (distance < score) {
      score = distance;
      bestIndex = index;
    }
  }
  return bestIndex;
};

export const EtaCalculationDialog = (props: Props) => {
  const destinationRows = useMemo(() => props.groupedDeliveries.filter((group) => (
    (!group.isPickUpPoint && group.delivery_type !== DeliveryType.WITHDRAW) ||
    (group.isPickUpPoint && group.delivery_type === DeliveryType.WITHDRAW)
  )), [props.groupedDeliveries]);

  const rows = useMemo(() => {
    return destinationRows.map((group, index) => {
      const previousRows = destinationRows.slice(0, index + 1);
      const totals = previousRows.reduce((current, item) => ({
        deliveries: current.deliveries + (item.deliveriesCount || 0),
        weight: current.weight + (item.weight || 0),
        packages: current.packages + (item.packages_number || 0),
        volume: current.volume + (item.volume || 0),
        cachets: current.cachets + (item.cachetCount || 0),
      }), {deliveries: 0, weight: 0, packages: 0, volume: 0, cachets: 0});
      const mappingIndex = getClosestPointIndex(group, props.pointsDurationMapping);
      const routingDuration = props.pointsDurationMapping[mappingIndex]
        ? props.pointsDurationMapping[mappingIndex].duration
        : 0;
      const routingDistance = props.pointsDurationMapping[mappingIndex]
        ? props.pointsDurationMapping[mappingIndex].distance || 0
        : 0;
      const routingComponent = Math.floor(routingDuration * props.etaParams.TIME_COEFF);
      const stopComponent = previousRows.length * props.etaParams.ONE_STEP_TIME;
      const multiStopComponent = (totals.deliveries - previousRows.length) * props.etaParams.MULTI_STEP_TIME;
      const weightComponent = totals.weight * props.etaParams.WEIGHT_COEFF;
      const packageComponent = totals.packages * props.etaParams.PACKAGE_COEFF;
      const volumeComponent = totals.volume * props.etaParams.VOLUME_COEFF;
      const cachetComponent = totals.cachets * props.etaParams.CASHET_COEFF;

      return {
        group,
        totals,
        routingDuration,
        routingDistance,
        routingComponent,
        stopComponent,
        multiStopComponent,
        weightComponent,
        packageComponent,
        volumeComponent,
        cachetComponent,
        etaDuration: routingComponent + stopComponent + multiStopComponent + weightComponent + packageComponent + volumeComponent + cachetComponent,
      };
    });
  }, [destinationRows, props.etaParams, props.pointsDurationMapping]);

  const returnRow = useMemo(() => {
    const totals = props.addedSegments.reduce((current, segment) => ({
      deliveries: current.deliveries + 1,
      weight: current.weight + (segment.delivery.weight || 0),
      packages: current.packages + (segment.delivery.packages_number || 0),
      volume: current.volume + (segment.delivery.volume || 0),
      cachets: current.cachets + (segment.delivery.cachet ? 1 : 0),
    }), {deliveries: 0, weight: 0, packages: 0, volume: 0, cachets: 0});
    const stops = destinationRows.length;
    const routingComponent = Math.floor(props.totalRoutingDuration * props.etaParams.TIME_COEFF);
    const stopComponent = stops * props.etaParams.ONE_STEP_TIME;
    const multiStopComponent = (totals.deliveries - stops) * props.etaParams.MULTI_STEP_TIME;
    const weightComponent = totals.weight * props.etaParams.WEIGHT_COEFF;
    const packageComponent = totals.packages * props.etaParams.PACKAGE_COEFF;
    const volumeComponent = totals.volume * props.etaParams.VOLUME_COEFF;
    const cachetComponent = totals.cachets * props.etaParams.CASHET_COEFF;

    return {
      totals,
      routingComponent,
      stopComponent,
      multiStopComponent,
      weightComponent,
      packageComponent,
      volumeComponent,
      cachetComponent,
      etaDuration: props.totalEtaDuration,
    };
  }, [destinationRows.length, props.addedSegments, props.etaParams, props.totalEtaDuration, props.totalRoutingDuration]);

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        Dettaglio calcolo ETA
        {props.auditEnabled && <Button onClick={props.onCapture} color="primary">Registra snapshot</Button>}
      </DialogTitle>
      <DialogContent>
        <Typography color={props.etaParamsLoaded ? "textPrimary" : "error"} paragraph>
          {props.etaParamsLoaded
            ? "Parametri ETA caricati dalla configurazione corrente."
            : "Attenzione: sono in uso i parametri di fallback mentre la configurazione ETA viene caricata."}
        </Typography>
        <Typography paragraph>
          Totale: {props.totalDistance} km · OSRM {formatDuration(props.totalRoutingDuration)} · ETA {formatDuration(props.totalEtaDuration)}
        </Typography>
        <Typography paragraph variant="body2">
          TIME_COEFF={props.etaParams.TIME_COEFF} · ONE_STEP_TIME={props.etaParams.ONE_STEP_TIME}s · MULTI_STEP_TIME={props.etaParams.MULTI_STEP_TIME}s · WEIGHT_COEFF={props.etaParams.WEIGHT_COEFF} · PACKAGE_COEFF={props.etaParams.PACKAGE_COEFF} · VOLUME_COEFF={props.etaParams.VOLUME_COEFF} · CASHET_COEFF={props.etaParams.CASHET_COEFF}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fermata</TableCell>
                <TableCell align="right">Km</TableCell>
                <TableCell align="right">OSRM</TableCell>
                <TableCell align="right">Fermate</TableCell>
                <TableCell align="right">Multi</TableCell>
                <TableCell align="right">Peso</TableCell>
                <TableCell align="right">Colli</TableCell>
                <TableCell align="right">Volume</TableCell>
                <TableCell align="right">C/ass</TableCell>
                <TableCell align="right">ETA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.group.id}>
                  <TableCell>
                    <Typography variant="body2">{row.group.receiver}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Missioni {row.totals.deliveries}, peso {row.totals.weight}, colli {row.totals.packages}, volume {row.totals.volume}, c/ass {row.totals.cachets}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{(row.routingDistance / 1000).toFixed(1)}</TableCell>
                  <TableCell align="right">{formatDuration(row.routingComponent)}</TableCell>
                  <TableCell align="right">{formatDuration(row.stopComponent)}</TableCell>
                  <TableCell align="right">{formatDuration(row.multiStopComponent)}</TableCell>
                  <TableCell align="right">{formatDuration(row.weightComponent)}</TableCell>
                  <TableCell align="right">{formatDuration(row.packageComponent)}</TableCell>
                  <TableCell align="right">{formatDuration(row.volumeComponent)}</TableCell>
                  <TableCell align="right">{formatDuration(row.cachetComponent)}</TableCell>
                  <TableCell align="right"><strong>{formatDuration(row.etaDuration)}</strong></TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <Typography variant="body2"><strong>Rientro{props.finalPointName ? ` · ${props.finalPointName}` : ""}</strong></Typography>
                  <Typography variant="caption" color="textSecondary">
                    Missioni {returnRow.totals.deliveries}, peso {returnRow.totals.weight}, colli {returnRow.totals.packages}, volume {returnRow.totals.volume}, c/ass {returnRow.totals.cachets}
                  </Typography>
                </TableCell>
                <TableCell align="right">{props.totalDistance.toFixed(1)}</TableCell>
                <TableCell align="right">{formatDuration(returnRow.routingComponent)}</TableCell>
                <TableCell align="right">{formatDuration(returnRow.stopComponent)}</TableCell>
                <TableCell align="right">{formatDuration(returnRow.multiStopComponent)}</TableCell>
                <TableCell align="right">{formatDuration(returnRow.weightComponent)}</TableCell>
                <TableCell align="right">{formatDuration(returnRow.packageComponent)}</TableCell>
                <TableCell align="right">{formatDuration(returnRow.volumeComponent)}</TableCell>
                <TableCell align="right">{formatDuration(returnRow.cachetComponent)}</TableCell>
                <TableCell align="right"><strong>{formatDuration(returnRow.etaDuration)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};
